import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Lightweight form for a person under an Account (see ContactViewModal) --
// deliberately not the full ContactFormModal: a person has a name/title/
// phone/email, not a street address or MC number. type/parent_contact_id
// are set here, never shown -- a person isn't itself a Customer/Shipper/
// Consignee, it belongs to one.
export default function ContactPersonFormModal({ person, accountId, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: person?.name || '',
    title: person?.title || '',
    primary_phone: person?.primary_phone || '',
    email: person?.email || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required.');
      return;
    }
    setSaving(true);
    try {
      if (person?.id) {
        await client.entities.Contact.update(person.id, form);
        toast.success('Contact updated.');
      } else {
        await client.entities.Contact.create({ ...form, parent_contact_id: accountId });
        toast.success('Contact added.');
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-md z-10 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-bold text-foreground text-lg">{person ? 'Edit Contact' : 'Add Contact'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Name *</label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Title</label>
            <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Dispatcher, Warehouse Manager" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Phone</label>
            <Input value={form.primary_phone} onChange={e => set('primary_phone', e.target.value)} placeholder="Phone" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Email</label>
            <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email" />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
            {saving ? 'Saving...' : person ? 'Save Changes' : 'Add Contact'}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-border">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
